import { useState } from 'react';

import { ICartEntry, IItem } from '@rbi-ctg/menu';
import { useCart } from 'hooks/use-cart';
import { useMenuContext } from 'state/menu';
import { createCartEntry } from 'utils/cart';

type useDonationProps = {
  donationItems: IItem[];
};

function useDonation({ donationItems }: useDonationProps) {
  const { cartEntries, addItemToCart, removeFromCart } = useCart();
  const { pricingFunction } = useMenuContext();

  const [selectedDonationCartEntry, setSelectedDonationCartEntry] = useState(() => {
    const preloadedDonationCartEntry = cartEntries.find(cartEntry => cartEntry.isDonation);
    if (preloadedDonationCartEntry && donationItems) {
      const isPreloadedDonationCartEntryValid = donationItems.some(
        item => item._id === preloadedDonationCartEntry._id
      );
      if (isPreloadedDonationCartEntryValid) {
        return preloadedDonationCartEntry;
      }
      removeFromCart({ cartId: preloadedDonationCartEntry.cartId });
    }
    return null;
  });

  function handleAddDonationCartEntry(donationItem: IItem) {
    if (selectedDonationCartEntry?._id === donationItem._id) {
      return;
    }

    if (selectedDonationCartEntry) {
      removeFromCart({ cartId: selectedDonationCartEntry.cartId });
    }

    const newDonationCartEntry: ICartEntry = {
      ...createCartEntry({
        item: donationItem,
        price: pricingFunction({ item: donationItem }),
      }),
      isDonation: true,
    };
    addItemToCart(newDonationCartEntry);
    setSelectedDonationCartEntry(newDonationCartEntry);
  }

  function handleRemoveDonationCartEntry({ cartId }: { cartId: string }) {
    removeFromCart({ cartId });
    setSelectedDonationCartEntry(null);
  }

  return {
    selectedDonationCartEntry,
    isDonationEnabled: !!selectedDonationCartEntry,
    handleAddDonationCartEntry,
    handleRemoveDonationCartEntry,
  };
}

export default useDonation;
